import { API_BASE_URL } from "../../../config";
import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import useFetchUserSeller from '../../Utils/useFetchUserSeller';
import { assignCustomerToRoute } from '../../Utils/assignCustomerRoute';
import { fetchRutineAll, buildRouteMap } from '../../Utils/customerListData';

const EditCustomerData = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { customerData, newLatitude, newLongitude } = state;

    const type = customerData.type || 'REGULAR';
    const raw = customerData.raw || {};

    // State for form inputs
    const [storeName, setStoreName] = useState(customerData.storeName);
    const [latitude, setLatitude] = useState(String(newLatitude || customerData.latitude || ''));
    const [longitude, setLongitude] = useState(String(newLongitude || customerData.longitude || ''));
    const [customerAvailable, setCustomerAvailable] = useState(
        customerData.customerAvailable === undefined ? true : !!customerData.customerAvailable
    );
    // phone: REGULAR + AGENT · business: REGULAR only · customerClass: REGULAR only, manual
    // override field distinct from the live-computed grade shown on /customerData (that one comes
    // from purchase-ranking on the fly and is never persisted back to this DB field).
    const [phone, setPhone] = useState(raw.phone || '');
    const [business, setBusiness] = useState(raw.business || '');
    const [customerClass, setCustomerClass] = useState(raw.customerClass || '');
    const CUSTOMER_CLASS_OPTIONS = ['S++', 'S+', 'S', 'A+', 'A', 'A-', 'B+', 'B', 'C+', 'C', 'C-', 'D+', 'D', 'D-', 'F'];
    const [error, setError] = useState('');

    // สายที่รับผิดชอบ (REGULAR only): routes the customer already has + one route to add on save.
    // Stored in customer_buy_rutine — same data as the ลูกค้าประจำสาย page and the list column.
    const handleLogoutToLogin = useCallback(() => {
        localStorage.removeItem('user');
        navigate('/login');
    }, [navigate]);
    const rawSellers = useFetchUserSeller(handleLogoutToLogin);
    const sellers = Array.isArray(rawSellers) ? rawSellers : [];
    const [currentRoutes, setCurrentRoutes] = useState(
        Array.isArray(customerData.assignedSellers) ? customerData.assignedSellers : []
    );
    const [newSellerId, setNewSellerId] = useState('');
    // 'loading' until the customer's real routes land (no "ยังไม่มีสาย", no adding a route yet —
    // guards against a duplicate add), 'error' if the load failed (also no adding: we can't tell
    // which routes already exist).
    const [routeStatus, setRouteStatus] = useState(type === 'REGULAR' ? 'loading' : 'ready');

    // The page can be opened from the list, the map popup or the waypoint view, and only the list
    // carries assignedSellers in its router state — so always load the customer's real routes
    // here (same loader as the list). State is only the initial value until this lands.
    useEffect(() => {
        if (type !== 'REGULAR') return;
        const token = checkToken();
        if (!token) return;
        let cancelled = false;
        fetchRutineAll(token).then((rows) => {
            // null = the call failed; [] is a real "no routes anywhere"
            if (!Array.isArray(rows)) {
                if (!cancelled) setRouteStatus('error');
                return;
            }
            return buildRouteMap(token, rows).then((routeMap) => {
                if (cancelled) return;
                setCurrentRoutes(routeMap[id] || []);
                setRouteStatus('ready');
            });
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, type]);

    // Check token for authentication
    const checkToken = useCallback(() => {
        const user = localStorage.getItem('user');
        if (!user) {
            navigate('/login');
            return null;
        }
        return JSON.parse(user)?.token;
    }, [navigate]);

    const buildUrlAndBody = () => {
        const base = `${API_BASE_URL}/customer/api/customers`;
        let url = '';
        let body = {};
        if (type === 'REGULAR') {
            url = `${base}/regular/${id}`;
            body = {
                storeName: storeName,
                phone,
                business,
                customerClass,
                latitude,
                longitude,
                operating: customerAvailable,
            };
        } else if (type === 'WALK_IN') {
            url = `${base}/walkin/${id}`;
            body = {
                storeName: storeName,
                latitude,
                longitude,
                operating: customerAvailable,
            };
        } else if (type === 'AGENT') {
            url = `${base}/agent/${id}`;
            body = {
                storeName: storeName,
                phone,
                latitude,
                longitude,
                operating: customerAvailable,
            };
        } else if (type === 'POTENTIAL') {
            url = `${base}/potential/${id}`;
            body = {
                storeName: storeName,
                business,
                latitude,
                longitude,
            };
        }
        return { url, body };
    };

    // Update customer
    const handleUpdate = async (e) => {
        e.preventDefault();
        const token = checkToken();
        if (!token) return;

        try {
            const { url, body } = buildUrlAndBody();
            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const text = await response.text();
                setError(`Failed to update customer: ${text}`);
                return;
            }

            if (type === 'REGULAR' && newSellerId && !currentRoutes.some(r => r.sellerUsername === newSellerId)) {
                try {
                    await assignCustomerToRoute(token, id, newSellerId);
                } catch (routeErr) {
                    setError(`บันทึกข้อมูลร้านแล้ว แต่ตั้งสายไม่สำเร็จ: ${routeErr.message}`);
                    return;
                }
            }

            navigate('/customerData');
        } catch (err) {
            setError('Error updating customer');
            console.error('Update error:', err);
        }
    };

    const handleRemoveRoute = async (route) => {
        if (!window.confirm(`เอาลูกค้านี้ออกจากสาย ${route.sellerName}?`)) return;
        const token = checkToken();
        if (!token) return;
        try {
            const resp = await fetch(`${API_BASE_URL}/customer/api/customer-buy-rutine/${route.sellerUsername}/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!resp.ok) {
                setError(`เอาออกจากสายไม่สำเร็จ: ${resp.status}`);
                return;
            }
            setCurrentRoutes(prev => prev.filter(r => r.sellerUsername !== route.sellerUsername));
        } catch (err) {
            setError('เอาออกจากสายไม่สำเร็จ');
        }
    };

    // Duplicate customer (create a new one with current fields)
    const handleDuplicate = async () => {
        const token = checkToken();
        if (!token) return;
        try {
            const type = customerData.type || 'REGULAR';
            const base = `${API_BASE_URL}/customer/api/customers`;
            let url = '';
            let body = {};
            if (type === 'REGULAR') {
                url = `${base}/regular`;
                body = {
                    storeName: storeName,
                    latitude,
                    longitude,
                    operating: customerAvailable,
                };
            } else if (type === 'WALK_IN') {
                url = `${base}/walkin`;
                body = {
                    storeName: storeName,
                    latitude,
                    longitude,
                    operating: customerAvailable,
                };
            } else if (type === 'AGENT') {
                url = `${base}/agent`;
                body = {
                    storeName: storeName,
                    latitude,
                    longitude,
                    operating: customerAvailable,
                };
            } else if (type === 'POTENTIAL') {
                url = `${base}/potential`;
                body = {
                    storeName: storeName,
                    latitude,
                    longitude,
                    operating: customerAvailable,
                };
            }

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const text = await response.text();
                setError(`Failed to create customer: ${text}`);
                return;
            }
            navigate('/customerData');
        } catch (err) {
            setError('Error creating customer');
            console.error('Duplicate error:', err);
        }
    };

    return (
        <div>
            <h1>แก้ไขข้อมูลร้าน</h1>
            <button onClick={() => navigate('/customerData')} className="backButton">
                กลับ
            </button>
            <br />
            <br />

            <form onSubmit={handleUpdate}>
                {error && <p className="error">{error}</p>}

                {/* Store Name Input */}
                <label>
                    ชื่อร้าน:
                    <input
                        type="text"
                        value={storeName}
                        onChange={(e) => setStoreName(e.target.value)}
                    />
                </label>

                {(type === 'REGULAR' || type === 'AGENT') && (
                    <label>
                        เบอร์โทร:
                        <input
                            type="text"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                        />
                    </label>
                )}

                {(type === 'REGULAR' || type === 'POTENTIAL') && (
                    <label>
                        ประเภทธุรกิจ:
                        <input
                            type="text"
                            value={business}
                            onChange={(e) => setBusiness(e.target.value)}
                        />
                    </label>
                )}

                {type === 'REGULAR' && (
                    <label>
                        เกรดลูกค้า (กำหนดเอง — แยกจากเกรดที่คำนวณอัตโนมัติในหน้ารายการ):
                        <select
                            value={customerClass}
                            onChange={(e) => setCustomerClass(e.target.value)}
                        >
                            <option value="">ไม่กำหนด</option>
                            {CUSTOMER_CLASS_OPTIONS.map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </label>
                )}

                {type === 'REGULAR' && (
                    <div className="route-assign">
                        <div>สายที่รับผิดชอบ:</div>
                        {routeStatus === 'loading' && <div data-testid="route-loading">กำลังโหลด...</div>}
                        {routeStatus === 'error' && <div data-testid="route-error">โหลดไม่สำเร็จ</div>}
                        {routeStatus === 'ready' && currentRoutes.length === 0 && <div data-testid="no-route">ยังไม่มีสาย</div>}
                        {currentRoutes.map(r => (
                            <div key={r.sellerUsername}>
                                {r.sellerName}{' '}
                                <button type="button" disabled={routeStatus !== 'ready'} onClick={() => handleRemoveRoute(r)}>เอาออก</button>
                            </div>
                        ))}
                        <label>
                            เพิ่มเข้าสาย:
                            <select
                                aria-label="เพิ่มเข้าสาย"
                                disabled={routeStatus !== 'ready'}
                                value={newSellerId}
                                onChange={(e) => setNewSellerId(e.target.value)}
                            >
                                <option value="">ไม่เปลี่ยนสาย</option>
                                {sellers
                                    .filter(s => !currentRoutes.some(r => r.sellerUsername === s.uid))
                                    .map(s => (
                                        <option key={s.uid} value={s.uid}>
                                            {s.nameuser} ({s.username})
                                        </option>
                                    ))}
                            </select>
                        </label>
                    </div>
                )}

                {/* Latitude Input */}
                <label>
                    Latitude:
                    <input
                        type="text"
                        value={latitude}
                        onChange={(e) => setLatitude(e.target.value)}
                    />
                </label>

                {/* Longitude Input */}
                <label>
                    Longitude:
                    <input
                        type="text"
                        value={longitude}
                        onChange={(e) => setLongitude(e.target.value)}
                    />
                </label>

                {/* Approval Status Dropdown — POTENTIAL has no operating field on the backend */}
                {type !== 'POTENTIAL' && (
                <label>
                    แสดงให้กับคนขาย:
                    <select
                        value={customerAvailable}
                        onChange={(e) => setCustomerAvailable(e.target.value === 'true')}
                    >
                        <option value="true">แสดง</option>
                        <option value="false">ไม่แสดง</option>
                    </select>
                </label>
                )}

                <button type="submit">บันทึก</button>
                {/* Optional: A duplicate button if needed */}
                {/* <button type="button" onClick={handleDuplicate}>ทำสำเนา</button> */}
            </form>
        </div>
    );
};

export default EditCustomerData;
